import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db, appId } from '../firebase';
import MessageBox from '../components/MessageBox';
import { SNIFFSPOT_LOGO_URL } from '../utils/constants';

function WeeklySchedulePage({ blockedDays }) {
    const [bookings, setBookings] = useState([]);
    const [loadingBookings, setLoadingBookings] = useState(true);
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('info');

    const [currentWeekStart, setCurrentWeekStart] = useState(() => {
        const today = new Date();
        const dayOfWeek = today.getDay();
        const diff = today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
        const monday = new Date(today.setDate(diff));
        monday.setHours(0, 0, 0, 0);
        return monday;
    });

    useEffect(() => {
        if (!db) return;

        setLoadingBookings(true);
        const bookingsColRef = collection(db, 'artifacts', appId, 'public', 'data', 'bookings');
        // Ensure only 'approved' bookings are shown on the public schedule
        const q = query(bookingsColRef, where('status', '==', 'approved')); 

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const fetchedBookings = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                startTime: doc.data().startTime.toDate(),
                endTime: doc.data().endTime.toDate()
            }));
            setBookings(fetchedBookings);
            setLoadingBookings(false);
        }, (error) => {
            console.error("Error fetching weekly schedule bookings:", error);
            setMessage("Error loading weekly schedule.");
            setMessageType("error");
            setLoadingBookings(false);
        });

        return () => unsubscribe();
    }, []);

    const getWeekDays = (startOfWeek) => {
        const days = [];
        for (let i = 0; i < 7; i++) {
            const day = new Date(startOfWeek);
            day.setDate(startOfWeek.getDate() + i);
            days.push(day);
        }
        return days;
    };

    const weekDays = getWeekDays(currentWeekStart);

    const generateDisplayTimeSlots = () => {
        const slots = [];
        // Change 7 to 6, and 18 to 20 to match original logic
        for (let h = 6; h < 20; h++) { 
            slots.push(`${h.toString().padStart(2, '0')}:00`);
            slots.push(`${h.toString().padStart(2, '0')}:30`);
        }
        return slots;
    };

    const displayTimeSlots = generateDisplayTimeSlots();

    const getSlotInfo = (date, timeSlot) => {
        const slotHour = parseInt(timeSlot.substring(0, 2));
        const slotMinute = parseInt(timeSlot.substring(3, 5));
        const currentSlotStart = new Date(date);
        currentSlotStart.setHours(slotHour, slotMinute, 0, 0);

        const blockedSlot = blockedDays.find(bs => 
            bs.startTime && bs.startTime.getTime() === currentSlotStart.getTime()
        );
        
        if (blockedSlot) {
            return { status: 'blocked', reason: blockedSlot.reason };
        }

        const slotBookings = bookings.filter(booking => {
            const bookingStart = booking.startTime;
            const isSameDay = bookingStart.getFullYear() === date.getFullYear() &&
                              bookingStart.getMonth() === date.getMonth() &&
                              bookingStart.getDate() === date.getDate();
            if (!isSameDay) return false;
            return bookingStart.getTime() === currentSlotStart.getTime();
        }).sort((a, b) => a.startTime.getTime() - b.startTime.getTime());

        return { status: 'booked', bookings: slotBookings };
    };

    const getShortName = (fullName) => {
        if (!fullName) return '';
        const parts = fullName.split(' ');
        return parts[0];
    };

    const handleWeekChange = (weeks) => {
        const newWeekStart = new Date(currentWeekStart);
        newWeekStart.setDate(newWeekStart.getDate() + (weeks * 7));
        setCurrentWeekStart(newWeekStart);
    };

    const handlePrint = () => {
        window.print();
    };

    if (loadingBookings) {
        return (
            <div className="flex items-center justify-center min-h-[calc(100vh-80px)] bg-gray-50">
                <div className="text-xl font-semibold text-gray-700">Loading schedule...</div>
            </div>
        );
    }

    return (
        <div className="container mx-auto p-6 bg-white rounded-lg shadow-lg my-6">
            <MessageBox message={message} type={messageType} onClose={() => setMessage('')} />
            
            <div className="print-container">
                <div className="print-only mb-4">
                    <div className="flex justify-between items-center border-b pb-2">
                         <img
                            src={SNIFFSPOT_LOGO_URL}
                            alt="Shep's Sniff Spot Logo"
                            className="h-10 w-auto"
                            onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/192x64/000/FFF?text=Sniffspot"; }}
                        />
                        <h2 className="text-xl font-bold text-gray-800 text-right">
                            Weekly Schedule<br/>
                            <span className="font-normal text-base">{currentWeekStart.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} - {new Date(currentWeekStart.getTime() + 6 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                        </h2>
                    </div>
                </div>

                {/* On-Screen View */}
                <div className="no-print">
                     <h2 className="font-display text-5xl text-gray-800 mb-6 text-center tracking-wider">
                        Weekly Schedule
                    </h2>

                    <div className="flex justify-center items-center space-x-4 mb-6">
                        <button
                            onClick={() => handleWeekChange(-1)}
                            className="p-3 rounded-full bg-blue-500 text-white shadow-md hover:bg-blue-600 transition duration-200"
                        >
                            &lt; Prev Week
                        </button>
                        <span className="text-2xl font-semibold text-gray-700">
                            {currentWeekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} -
                            {new Date(currentWeekStart.getTime() + 6 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                        <button
                            onClick={() => handleWeekChange(1)}
                            className="p-3 rounded-full bg-blue-500 text-white shadow-md hover:bg-blue-600 transition duration-200"
                        >
                            Next Week &gt;
                        </button>
                    </div>

                    <div className="overflow-x-auto schedule-table-wrapper">
                        <div className="grid w-full" style={{ gridTemplateColumns: `minmax(50px, 1fr) repeat(${displayTimeSlots.length}, minmax(30px, 1fr))` }}>
                            <div className="bg-gray-100 p-3 font-bold text-gray-700 border-r border-b border-gray-200 text-center text-sm">Date</div>
                            {displayTimeSlots.map((timeSlot, index) => (
                                <div key={index} className="bg-gray-100 p-3 font-bold text-gray-700 border-r border-b border-gray-200 text-center text-sm schedule-table-time-cell flex justify-center items-center">
                                    <span className="vertical-text">{timeSlot}</span>
                                </div>
                            ))}

                            {weekDays.map((day, dayIndex) => (
                                <React.Fragment key={dayIndex}>
                                    <div className="bg-gray-50 p-3 text-gray-600 border-r border-b border-gray-200 text-sm font-medium flex flex-col justify-center items-center text-center">
                                        {day.toLocaleDateString('en-US', { weekday: 'short' })}<br/>{day.getDate()}
                                    </div>
                                    {displayTimeSlots.map((timeSlot, timeIndex) => {
                                        const slotInfo = getSlotInfo(day, timeSlot);
                                        const isBlocked = slotInfo.status === 'blocked';
                                        
                                        let cellContent;
                                        let cellClasses = `p-2 border-r border-b border-gray-200 min-h-[56px] flex flex-col justify-center items-center`;
                                        let cellTitle = '';

                                        if (isBlocked) {
                                            cellClasses += " bg-red-100 text-red-800 cursor-not-allowed flex-col space-y-1";
                                            cellTitle = slotInfo.reason;
                                            cellContent = (
                                                <div className="vertical-text font-bold text-xs tracking-wide opacity-90 whitespace-nowrap">
                                                    <span 
                                                        className="block text-xs text-red-700 opacity-90 leading-tight mt-1 text-center"
                                                        title={slotInfo.reason}
                                                    >
                                                        ({slotInfo.reason.split(' ').slice(0, 2).join(' ') + (slotInfo.reason.split(' ').length > 2 ? '...' : '')})
                                                    </span>
                                                </div>
                                            );
                                        } else {
                                            cellContent = slotInfo.bookings.map(booking => (
                                                <div
                                                    key={booking.id}
                                                    className="rounded-md p-1 my-0.5 text-xs font-semibold text-white truncate shadow-sm vertical-text"
                                                    style={{ backgroundColor: booking.userColor }}
                                                    title={`${booking.userName} (${booking.startTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })})`}
                                                >
                                                    {getShortName(booking.userName)} 
                                                </div>
                                            ));
                                        }
                                        
                                        return (
                                            <div
                                                key={`${dayIndex}-${timeIndex}`}
                                                className={cellClasses}
                                                title={cellTitle}
                                            >
                                                {cellContent}
                                            </div>
                                        );
                                    })}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Print-Only View */}
                <div className="print-only">
                    {weekDays.map(day => {
                        const dayEvents = [
                            ...bookings.filter(b => b.startTime.toDateString() === day.toDateString()),
                            ...blockedDays.filter(b => b.startTime.toDateString() === day.toDateString())
                        ].sort((a, b) => a.startTime - b.startTime);

                        return (
                            <div key={day.toISOString()} className="mb-4">
                                <h3 className="text-lg font-bold border-b-2 border-gray-300 pb-1 mb-2">
                                    {day.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                                </h3>
                                {dayEvents.length > 0 ? (
                                    <ul className="space-y-1">
                                        {dayEvents.map(event => (
                                            <li key={event.id || event.startTime.toISOString()} className="flex items-center text-sm">
                                                <span className="w-40 font-mono text-gray-600">
                                                    {event.startTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - {event.endTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                </span>
                                                {event.userName ? (
                                                    <span className="font-semibold" style={{ color: event.userColor }}>{event.userName}</span>
                                                ) : (
                                                    <span className="text-gray-700">Not Available ({event.reason})</span>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="text-sm text-gray-500 italic">No bookings or blocked slots.</p>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="mt-6 text-center text-gray-600 text-sm no-print">
                Bookings are 30 minutes long. Schedule displayed from 6 AM to 8 PM.
            </div>
            <div className="flex justify-center mt-6 no-print">
                <button
                    onClick={handlePrint}
                    className="px-6 py-3 bg-blue-600 text-white font-bold rounded-full shadow-xl hover:bg-blue-700 transition duration-300 ease-in-out transform hover:scale-105"
                >
                    Print Schedule
                </button>
            </div>
        </div>
    );
}

export default WeeklySchedulePage;